/**
 * PublicWaveSharePage — unauthenticated `/wave/:token` page rendered when
 * a brand rep shares a wave performance link externally (e.g. forwarded
 * to a CMO, DTC investor, or retail partner).
 *
 * Route: /wave/:token (public, no ProtectedRoute wrapper).
 * Pulls summary from `GET /api/public/waves/{token}`.
 * Deep-links to the PDF: `/api/public/waves/{token}/export.pdf`.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Waves, Download, Activity, Users, Radio, TrendingUp, Heart, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtNum = (n) => {
  const v = Number(n || 0);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return String(v);
};

export default function PublicWaveSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/waves/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e?.response?.status === 410 ? 'This share link has expired.'
              : e?.response?.status === 404 ? 'This share link was revoked or never existed.'
              : 'Could not load the wave report.');
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="public-wave-error">
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF4D94] font-bold mb-3">Wave Report</p>
          <p className="text-lg text-gray-300 font-semibold">{error}</p>
          <Link to="/" className="inline-block mt-6 text-[11px] text-[#00D4FF] font-bold hover:underline">
            Visit wavedsp.ai →
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-gray-500 flex items-center justify-center text-sm animate-pulse">
        Loading wave report…
      </div>
    );
  }

  const { brand_name, wave, totals, pdf_url } = data;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="public-wave-share-page">
      <TopBar />
      <main className="max-w-[820px] mx-auto px-5 lg:px-8 py-10 space-y-10">

        {/* Hero */}
        <section>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF4D94] font-bold">Wave Performance Report</p>
          <h1 className="text-3xl sm:text-4xl font-black leading-[1.05] tracking-tight mt-2"
              style={{ background: 'linear-gradient(180deg,#fff 0%,#C9CCD4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {wave.name}
          </h1>
          <p className="text-sm text-gray-400 mt-3">
            A <span className="text-white font-bold">{brand_name}</span> wave orchestrated through WAVE
            {wave.unique_hook && <>, launched around <span className="text-[#FF4D94] font-mono">{wave.unique_hook}</span></>}.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <Pill icon={Activity} label="Status" value={(wave.status || '-').toUpperCase()} />
            {wave.velocity_band && <Pill icon={TrendingUp} label="Band" value={wave.velocity_band} />}
            <Pill icon={Users} label="Creators" value={wave.creator_count} />
          </div>
        </section>

        {/* Totals */}
        <section>
          <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold mb-3">Live totals</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="public-totals">
            <Stat icon={Radio}       label="Posts fired"   value={`${totals.posts_fired}/${totals.posts_total}`} />
            <Stat icon={TrendingUp}  label="Reach"         value={fmtNum(totals.reach)} color="#00D4FF" />
            <Stat icon={Heart}       label="Engagements"   value={fmtNum(totals.engagements)} color="#FF4D94" />
            <Stat icon={Activity}    label="Eng. rate"     value={`${totals.engagement_rate}%`} color="#4ade80" />
          </div>
        </section>

        {/* Download + trust strip */}
        <section className="rounded-2xl border border-[#1f1f1f] bg-[#0F0F0F] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between" data-testid="public-pdf-cta">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#00D4FF] font-bold mb-1">Full report</p>
            <p className="text-base font-bold text-white">Download the PDF report for this wave.</p>
            <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">
              Includes top-performing creators, Flight-Deck optimization log, burst roll-up, and KPI benchmarks.
            </p>
          </div>
          <a
            href={`${API}${pdf_url}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="public-pdf-download"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#FF4D94] text-white font-black text-[12px] hover:brightness-110 whitespace-nowrap"
            style={{ boxShadow: '0 0 24px rgba(255,77,148,0.35)' }}
          >
            <Download className="h-4 w-4" /> Download PDF
          </a>
        </section>

        {/* Marketing strip — turns every shared link into a conversion loop */}
        <section className="rounded-2xl border border-[#1f1f1f] bg-gradient-to-br from-[#00D4FF]/8 to-[#FF4D94]/6 p-6" data-testid="public-marketing-strip">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF4D94] font-bold">The platform behind this report</p>
          <h2 className="text-xl font-black text-white mt-2 tracking-tight">WAVE: the DSP for creator-powered waves.</h2>
          <p className="text-[12px] text-gray-400 leading-relaxed mt-2 max-w-prose">
            Brands orchestrate cascading waves across their contracted creator roster. Every wave passes 10 stages
            of orchestration; every optimization lever is logged; every report is live.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {/* iter-90: removed public "Investor data room" link — surface taken
                down from public viewing. The "closed beta" line remains as the
                primary signal that the platform is invite-only. */}
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
              Closed beta. Accounts by invitation.
            </span>
          </div>
        </section>

        <footer className="pt-6 border-t border-[#1f1f1f] text-[10px] text-gray-600 flex items-center justify-between">
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="h-3 w-3 text-[#00D4FF]" />
            Private share · expires {data.expires_at ? new Date(data.expires_at).toLocaleDateString() : 'soon'}
          </span>
          <span>wavedsp.ai</span>
        </footer>
      </main>
    </div>
  );
}

function TopBar() {
  return (
    <header className="border-b border-[#1f1f1f] bg-[#0A0A0A] sticky top-0 z-10 backdrop-blur">
      <div className="max-w-[820px] mx-auto px-5 lg:px-8 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Waves className="h-4 w-4 text-[#00D4FF]" />
          <p className="text-sm font-black tracking-tight">
            WAVE <span className="text-gray-500 font-normal">Shared Report</span>
          </p>
        </div>
        {/* iter-90: top-bar wordmark is no longer a link — the /investor Data
            Room was taken down from public viewing, so the wordmark is now
            plain text. */}
        <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500" data-testid="public-wordmark">
          wavedsp.ai
        </span>
      </div>
    </header>
  );
}

function Pill({ icon: Icon, label, value }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold bg-[#0F0F0F] border border-[#1f1f1f]">
      <Icon className="h-3 w-3 text-[#00D4FF]" />
      <span className="text-gray-500">{label}</span>
      <span className="text-white font-black tabular-nums">{value}</span>
    </span>
  );
}

function Stat({ icon: Icon, label, value, color = '#fff' }) {
  return (
    <div className="rounded-xl p-4 bg-[#0F0F0F] border border-[#1f1f1f]">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3 w-3" style={{ color }} />
        <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold">{label}</p>
      </div>
      <p className="text-2xl font-black tabular-nums leading-none mt-1" style={{ color }}>{value}</p>
    </div>
  );
}
